import { AddMemberInput } from '@types';
import { FeeCategoryDataType, TermsList } from 'types/groups';

export const getTermEndDate = (startDate: string, duration: number) => {
  const termStartDate = new Date(startDate);
  if (process.env.REACT_APP_CUSTOM_TERM_DURATION === 'days') {
    return new Date(
      termStartDate.setDate(termStartDate.getDate() + Number(duration) - 1)
    ).toString();
  }
  return new Date(
    new Date(
      termStartDate.setMonth(termStartDate.getMonth() + Number(duration))
    ).setDate(termStartDate.getDate() - 1)
  ).toString();
};

export const getTermStartDateLimit = () => {
  const termDuration = 4;
  const termStartDate = new Date();
  if (process.env.REACT_APP_CUSTOM_TERM_DURATION === 'days') {
    return new Date(
      termStartDate.setDate(termStartDate.getDate() - Number(termDuration) + 1)
    ).toString();
  }

  return new Date(
    new Date(
      termStartDate.setMonth(termStartDate.getMonth() - Number(termDuration))
    ).setDate(1)
  ).toString();
};

export const initialTermsList: TermsList = {
  id: Math.random().toString(16).slice(2),
  term: 'Term 1',
  termDuration: 1,
  dueDate: new Date().toString(),
  termStartDate: new Date().toString(),
  termEndDate: getTermEndDate(new Date().toString(), 1),
  dueAmount: '0'
};

const defaultTermCategories = (
  feeCategoryData: {
    id: number;
    label: string;
    value: string;
  }[]
) =>
  feeCategoryData.map((item: any) => ({
    branch_fee_category_id: item.id,
    account_detail_id: item.id,
    category: item.label,
    amount: item.value || '0'
  }));

export const onAddTerm = (
  termsList: TermsList[],
  feeCategories: FeeCategoryDataType[],
  groupEndDate?: string
) => {
  const id = Math.random().toString(16).slice(2);
  const lastTermEndDate = new Date(termsList[termsList.length - 1].termEndDate);
  const termStartDate = new Date(
    lastTermEndDate.setDate(lastTermEndDate.getDate() + 1)
  ).toISOString();

  const termEndDate = getTermEndDate(
    termStartDate,
    initialTermsList.termDuration
  );

  return {
    endDateError: groupEndDate
      ? new Date(new Date(groupEndDate).setHours(0, 0, 0, 0)) <
        new Date(new Date(termEndDate).setHours(0, 0, 0, 0))
      : false,
    term: [
      ...termsList,
      {
        ...initialTermsList,
        id,
        term: `Term ${termsList.length + 1}`,
        dueDate: termStartDate,
        termStartDate,
        termEndDate,
        feeCategoryData: defaultTermCategories(feeCategories)
      }
    ]
  };
};

export const onTermDelete = (
  termId: string,
  termsList: TermsList[],
  groupEndDate?: string
) => {
  const updatedTermsList = termsList.filter(
    (termDetails) => termDetails.id !== termId
  );

  let prevTermEndDate = new Date(updatedTermsList[0].termEndDate);

  const finalTermsList = updatedTermsList.map((termDetails, index) => {
    if (index === 0) return termDetails;

    const termStartDate = new Date(
      prevTermEndDate.setDate(prevTermEndDate.getDate() + 1)
    ).toISOString();
    const termEndDate = getTermEndDate(
      termStartDate,
      Number(termDetails.termDuration)
    );
    prevTermEndDate = new Date(termEndDate);

    const dueDate = termStartDate;

    return {
      ...termDetails,
      term: `Term ${index + 1}`,
      termStartDate,
      termEndDate,
      dueDate
    };
  });

  const totalAmount = finalTermsList.reduce(
    (sum, termDetails) => sum + Number(termDetails.dueAmount),
    0
  );
  const endDateError = groupEndDate
    ? new Date(new Date(groupEndDate).setHours(0, 0, 0, 0)) <
      new Date(
        new Date(
          updatedTermsList[updatedTermsList.length - 1].termEndDate
        ).setHours(0, 0, 0, 0)
      )
    : false;

  return {
    endDateError,
    termsList: finalTermsList,
    totalAmount
  };
};

export const isDateBefore = (date1: string | Date, date2: string | Date) =>
  new Date(new Date(date1).setHours(0, 0, 0, 0)) <
  new Date(new Date(date2).setHours(0, 0, 0, 0));

export const customTermFileValidation = (
  feeCategories: string[],
  termsName: string[],
  member: AddMemberInput,
  index: number,
  minAmount: number,
  maxAmount: number
) => {
  const errors: string[] = [];
  let totalAmount = 0;
  let isCategoryValueEmpty = false;

  termsName.forEach((term) => {
    let termAmount = 0;

    if (feeCategories.length === 0) {
      const termKey = `${term}_Amount`;
      termAmount = Number(member[termKey as keyof AddMemberInput]);
      isCategoryValueEmpty = termAmount === undefined || termAmount === null;
      if (termAmount && Number(termAmount).toLocaleString() === 'NaN') {
        errors.push(`Row ${index + 1}: Invalid amount for ${term}`);
      }
      if (termAmount > maxAmount) {
        errors.push(
          `Row ${
            index + 1
          }: Term amount should be less than ${maxAmount} for ${term}`
        );
      }
    } else {
      feeCategories.forEach((category) => {
        const categoryKey = `${term}_${category.replace(/\s+/g, '')}`;
        const categoryValue = member[categoryKey as keyof AddMemberInput];
        isCategoryValueEmpty =
          categoryValue === undefined || categoryValue === null;

        if (categoryValue && Number(categoryValue).toLocaleString() === 'NaN') {
          errors.push(`Row ${index + 1}: Invalid amount for ${category}`);
        }
        termAmount += Number(categoryValue);
      });
    }

    if (!isCategoryValueEmpty && termAmount > maxAmount) {
      errors.push(
        `Row ${
          index + 1
        }: Term amount should be less than ${maxAmount} for ${term}`
      );
    }
    totalAmount += termAmount;
  });

  if (!isCategoryValueEmpty && totalAmount < minAmount) {
    errors.push(
      `Row ${index + 1}: Total term amount should be atleast ${minAmount}`
    );
  }

  return errors;
};

export const getUpdatedTermsList = (
  termsList: TermsList[],
  termId: string,
  field: string,
  value: any,
  groupEndDate?: string
) => {
  // This function is used to find the term start and end date
  // based on the term duration
  // also update due date if it is not in the range of term start and end date
  const updatedIndex = termsList.findIndex((term) => term.id === termId);
  let prevTermEndDate = '';
  let endDateError = false;

  const updatedTermsList = termsList.map((termDetails, index) => {
    let { termStartDate, termEndDate, termDuration, dueDate } = termDetails;

    if (termDetails.id === termId) {
      termDuration =
        field === 'termDuration' ? value : termDetails.termDuration;
      termStartDate =
        field === 'termStartDate' ? value : termDetails.termStartDate;
      termEndDate = getTermEndDate(termStartDate, termDuration);

      if (
        dueDate &&
        (new Date(dueDate).setHours(0, 0, 0, 0) >
          new Date(termEndDate).setHours(0, 0, 0, 0) ||
          new Date(dueDate).setHours(0, 0, 0, 0) <
            new Date(termStartDate).setHours(0, 0, 0, 0))
      ) {
        dueDate = termStartDate;
      }
      prevTermEndDate = termEndDate;
    } else if (index > updatedIndex) {
      const prevTermEndDateAsDate = new Date(prevTermEndDate);
      termStartDate = new Date(
        prevTermEndDateAsDate.setDate(prevTermEndDateAsDate.getDate() + 1)
      ).toISOString();
      termEndDate = getTermEndDate(termStartDate, Number(termDuration));

      if (
        dueDate &&
        (new Date(dueDate) > new Date(termEndDate) ||
          new Date(dueDate) < new Date(termStartDate))
      ) {
        dueDate = termStartDate;
      }
      prevTermEndDate = termEndDate;
    }
    if (
      groupEndDate &&
      new Date(new Date(groupEndDate).setHours(0, 0, 0, 0)) <
        new Date(new Date(termEndDate).setHours(0, 0, 0, 0))
    ) {
      endDateError = true;
    } else {
      endDateError = false;
    }
    return index >= updatedIndex
      ? {
          ...termDetails,
          termDuration,
          termStartDate,
          termEndDate,
          dueDate
        }
      : termDetails;
  });

  return { updatedTermsListData: updatedTermsList, dateError: endDateError };
};
